/**
 * Membership Card Configuration Models
 * 
 * Defines the structure for configurable membership cards and stickers.
 * Admins can configure which fields appear on cards and how they're displayed.
 */

import { Timestamp } from 'firebase/firestore';

/**
 * Field types supported on membership cards
 */
export type CardFieldType = 'text' | 'date' | 'badge' | 'qrcode' | 'image' | 'logo';

/**
 * Data source for field values
 */
export type CardFieldSource = 'membership' | 'user' | 'computed' | 'shared' | 'profile';

/**
 * Horizontal alignment options
 */
export type HorizontalAlignment = 'left' | 'center' | 'right';

/**
 * Vertical alignment options
 */
export type VerticalAlignment = 'top' | 'middle' | 'bottom';

/**
 * Grid position definition
 */
export interface GridPosition {
  column: number;      // Grid column position (1-based)
  row: number;         // Grid row position (1-based)
  columnSpan: number;  // How many columns to span
  rowSpan: number;     // How many rows to span
}

/**
 * Individual field configuration for membership cards
 */
export interface MembershipCardField {
  /** Unique identifier for the field */
  id: string;
  
  /** Internal field name (e.g., "memberName") */
  name: string;
  
  /** Display label shown on card (e.g., "Member Name") */
  label: string;
  
  /** Type of field (text, date, badge, qrcode, image) */
  type: CardFieldType;
  
  /** Where to get the data from */
  source: CardFieldSource;
  
  /** Field name in source collection (e.g., "firstName") */
  sourceField?: string;
  
  /** Default value if source field is empty */
  defaultValue?: string;
  
  /** Date format string (for date fields, e.g., "MM/DD/YYYY") */
  format?: string;
  
  /** Display order (lower numbers appear first) */
  order: number;
  
  /** Include this field on full membership card */
  showOnCard: boolean;
  
  /** Include this field on compact sticker */
  showOnSticker: boolean;
  
  /** Show field label on card */
  showLabelOnCard?: boolean;
  
  /** Show field label on sticker */
  showLabelOnSticker?: boolean;
  
  /** Field must have a value to display card */
  required: boolean;
  
  /** Can officers edit this field value */
  editable: boolean;
  
  /** Optional styling overrides */
  styles?: {
    fontSize?: string;
    fontWeight?: string;
    color?: string;
    horizontalAlignment?: HorizontalAlignment;
    verticalAlignment?: VerticalAlignment;
  };
  
  /** Grid positioning for card template */
  gridPositionCard?: GridPosition;
  
  /** Grid positioning for sticker template */
  gridPositionSticker?: GridPosition;
}

/**
 * Layout system for fields
 */
export type FieldLayout = 'grid' | 'flex' | 'absolute';

/**
 * Card orientation
 */
export type CardOrientation = 'portrait' | 'landscape';

/**
 * Dimension units
 */
export type DimensionUnit = 'px' | 'mm' | 'in';

/**
 * Card template configuration
 */
export interface CardTemplate {
  /** Template identifier */
  id: 'card' | 'sticker';
  
  /** Template display name */
  name: string;
  
  /** Physical/digital dimensions */
  dimensions: {
    width: number;
    height: number;
    unit: DimensionUnit;
  };
  
  /** Layout configuration */
  layout: {
    orientation: CardOrientation;
    fieldLayout: FieldLayout;
    gridColumns?: number;       // Number of columns in grid layout
    gridRows?: number;          // Number of rows in grid layout
    showGridLines?: boolean;    // Show grid lines in preview/editor
    padding?: number;           // Padding in pixels for the card content area
  };
  
  /** Visual styling */
  styling: {
    backgroundColor: string;
    borderColor: string;
    borderWidth: number;
    borderRadius: number;
    fontFamily: string;
    primaryColor: string;       // For headings, important text
    secondaryColor: string;     // For labels, less important text
  };
  
  /** Array of field IDs to include in this template */
  fields: string[];
}

/**
 * Complete membership card configuration
 * Stored in Firestore: membershipCardConfig/default
 */
export interface MembershipCardConfig {
  /** Array of all available fields */
  fields: MembershipCardField[];
  
  /** Full card template configuration */
  cardTemplate: CardTemplate;
  
  /** Compact sticker template configuration */
  stickerTemplate: CardTemplate;
  
  /** When configuration was created */
  createdAt?: Timestamp;
  
  /** When configuration was last updated */
  updatedAt?: Timestamp;
  
  /** User ID of last updater */
  updatedBy?: string;
  
  /** Schema version for migrations */
  version: number;
}

/**
 * Default field configurations
 * These are the initial fields created when setting up the card system
 */
export const DEFAULT_FIELDS: MembershipCardField[] = [
  {
    id: 'logo',
    name: 'logo',
    label: 'Club Logo',
    type: 'logo',
    source: 'shared',
    order: 0,
    showOnCard: true,
    showOnSticker: true,
    showLabelOnCard: false,
    showLabelOnSticker: false,
    required: false,
    editable: false,
    gridPositionCard: {
      column: 1,
      row: 1,
      columnSpan: 4,
      rowSpan: 1
    },
    gridPositionSticker: {
      column: 1,
      row: 1,
      columnSpan: 3,
      rowSpan: 1
    }
  },
  {
    id: 'member-name',
    name: 'memberName',
    label: 'Member Name',
    type: 'text',
    source: 'profile',
    sourceField: 'firstName,lastName',
    order: 1,
    showOnCard: true,
    showOnSticker: true,
    required: true,
    editable: false,
    styles: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      horizontalAlignment: 'center'
    },
    gridPositionCard: {
      column: 1,
      row: 2,
      columnSpan: 4,
      rowSpan: 1
    },
    gridPositionSticker: {
      column: 1,
      row: 2,
      columnSpan: 3,
      rowSpan: 1
    }
  },
  {
    id: 'membership-id',
    name: 'membershipId',
    label: 'Member ID',
    type: 'text',
    source: 'membership',
    sourceField: 'id',
    order: 2,
    showOnCard: true,
    showOnSticker: true,
    required: true,
    editable: false,
    styles: {
      fontSize: '0.875rem',
      horizontalAlignment: 'center'
    },
    gridPositionCard: {
      column: 1,
      row: 3,
      columnSpan: 2,
      rowSpan: 1
    },
    gridPositionSticker: {
      column: 1,
      row: 3,
      columnSpan: 1,
      rowSpan: 1
    }
  },
  {
    id: 'ama-number',
    name: 'amaNumber',
    label: 'AMA #',
    type: 'text',
    source: 'profile',
    sourceField: 'amaMembershipId',
    order: 3,
    showOnCard: true,
    showOnSticker: false,
    required: false,
    editable: true,
    gridPositionCard: {
      column: 1,
      row: 4,
      columnSpan: 2,
      rowSpan: 1
    }
  },
  {
    id: 'flight-level',
    name: 'flightLevel',
    label: 'Flight Level',
    type: 'badge',
    source: 'profile',
    sourceField: 'flightLevel',
    order: 4,
    showOnCard: true,
    showOnSticker: false,
    required: false,
    editable: false,
    gridPositionCard: {
      column: 3,
      row: 3,
      columnSpan: 2,
      rowSpan: 1
    }
  },
  {
    id: 'expiration',
    name: 'expirationDate',
    label: 'Expires',
    type: 'date',
    source: 'membership',
    sourceField: 'renewalDate',
    format: 'MM/DD/YYYY',
    order: 5,
    showOnCard: true,
    showOnSticker: true,
    required: true,
    editable: false,
    styles: {
      fontSize: '1rem',
      fontWeight: '600'
    },
    gridPositionCard: {
      column: 3,
      row: 4,
      columnSpan: 2,
      rowSpan: 1
    },
    gridPositionSticker: {
      column: 2,
      row: 3,
      columnSpan: 2,
      rowSpan: 1
    }
  },
  {
    id: 'status',
    name: 'status',
    label: 'Status',
    type: 'badge',
    source: 'membership',
    sourceField: 'status',
    order: 6,
    showOnCard: true,
    showOnSticker: false,
    required: true,
    editable: false,
    gridPositionCard: {
      column: 1,
      row: 5,
      columnSpan: 2,
      rowSpan: 1
    }
  },
  {
    id: 'join-date',
    name: 'joinDate',
    label: 'Member Since',
    type: 'date',
    source: 'profile',
    sourceField: 'joinDate',
    format: 'YYYY',
    order: 7,
    showOnCard: true,
    showOnSticker: false,
    required: false,
    editable: false,
    styles: {
      fontSize: '0.875rem'
    },
    gridPositionCard: {
      column: 3,
      row: 5,
      columnSpan: 2,
      rowSpan: 1
    }
  },
  {
    id: 'qr-code',
    name: 'qrCode',
    label: 'QR Code',
    type: 'qrcode',
    source: 'computed',
    order: 99,
    showOnCard: true,
    showOnSticker: false,
    required: false,
    editable: false,
    gridPositionCard: {
      column: 1,
      row: 6,
      columnSpan: 4,
      rowSpan: 1
    }
  }
];

/**
 * Default card template
 * Standard membership card layout
 */
export const DEFAULT_CARD_TEMPLATE: CardTemplate = {
  id: 'card',
  name: 'Membership Card',
  dimensions: {
    width: 400,
    height: 250,
    unit: 'px'
  },
  layout: {
    orientation: 'landscape',
    fieldLayout: 'grid',
    gridColumns: 4,
    gridRows: 6,
    padding: 24
  },
  styling: {
    backgroundColor: '#FFFFFF',
    borderColor: '#A8201A',
    borderWidth: 2,
    borderRadius: 8,
    fontFamily: 'PT Sans, sans-serif',
    primaryColor: '#A8201A',
    secondaryColor: '#64748b'
  },
  fields: [
    'logo',
    'member-name',
    'membership-id',
    'ama-number',
    'flight-level',
    'expiration',
    'status',
    'join-date',
    'qr-code'
  ]
};

/**
 * Default sticker template
 * Compact sticker for AMA cards
 */
export const DEFAULT_STICKER_TEMPLATE: CardTemplate = {
  id: 'sticker',
  name: 'Membership Sticker',
  dimensions: {
    width: 200,
    height: 100,
    unit: 'px'
  },
  layout: {
    orientation: 'landscape',
    fieldLayout: 'grid',
    gridColumns: 3,
    gridRows: 3,
    padding: 12
  },
  styling: {
    backgroundColor: '#FFFFFF',
    borderColor: '#A8201A',
    borderWidth: 1,
    borderRadius: 4,
    fontFamily: 'PT Sans, sans-serif',
    primaryColor: '#A8201A',
    secondaryColor: '#64748b'
  },
  fields: [
    'logo',
    'member-name',
    'membership-id',
    'expiration'
  ]
};

/**
 * Default complete configuration
 * Used when initializing the card system
 */
export const DEFAULT_CONFIG: MembershipCardConfig = {
  fields: DEFAULT_FIELDS,
  cardTemplate: DEFAULT_CARD_TEMPLATE,
  stickerTemplate: DEFAULT_STICKER_TEMPLATE,
  version: 1
};

/**
 * Type guard to check if a field is of type date
 */
export function isDateField(field: MembershipCardField): boolean {
  return field.type === 'date';
}

/**
 * Type guard to check if a field is of type badge
 */
export function isBadgeField(field: MembershipCardField): boolean {
  return field.type === 'badge';
}

/**
 * Type guard to check if a field is of type qrcode
 */
export function isQRCodeField(field: MembershipCardField): boolean {
  return field.type === 'qrcode';
}

/**
 * Helper to get field value from appropriate data source
 */
export function getFieldValue(
  membershipData: any,
  field: MembershipCardField,
  sharedData?: Record<string, any>,
  userData?: any,
  profileData?: any
): string | undefined {
  let rawValue: any;
  
  // Handle shared (membershipInfo) fields
  if (field.source === 'shared') {
    if (sharedData && field.sourceField) {
      rawValue = sharedData[field.sourceField] || field.defaultValue;
    } else {
      rawValue = field.defaultValue;
    }
  }
  
  // Handle user profile fields
  else if (field.source === 'user') {
    if (userData && field.sourceField) {
      rawValue = userData[field.sourceField] || field.defaultValue;
    } else {
      rawValue = field.defaultValue;
    }
  }
  
  // Handle profile fields
  else if (field.source === 'profile') {
    if (field.sourceField?.includes(',')) {
      // Combine multiple fields (e.g., firstName,lastName from profile)
      const fields = field.sourceField.split(',');
      const values = fields
        .map(f => profileData?.[f.trim()])
        .filter(Boolean);
      rawValue = values.join(' ') || field.defaultValue;
    } else if (profileData && field.sourceField) {
      rawValue = profileData[field.sourceField] || field.defaultValue;
    } else {
      rawValue = field.defaultValue;
    }
  }
  
  // Handle computed fields (legacy - prefer using specific sources)
  else if (field.source === 'computed') {
    if (field.sourceField?.includes(',')) {
      // Combine multiple fields - try profile first, then membership
      const fields = field.sourceField.split(',');
      const values = fields
        .map(f => profileData?.[f.trim()] || membershipData?.[f.trim()])
        .filter(Boolean);
      rawValue = values.join(' ') || field.defaultValue;
    } else if (field.sourceField) {
      // Single computed field expression - try profile first, then membership
      rawValue = profileData?.[field.sourceField] || membershipData?.[field.sourceField] || field.defaultValue;
    } else {
      rawValue = field.defaultValue;
    }
  }
  
  // Handle membership fields (default source)
  else if (field.source === 'membership' || !field.source) {
    if (membershipData && field.sourceField) {
      rawValue = membershipData[field.sourceField] || field.defaultValue;
    } else {
      rawValue = field.defaultValue;
    }
  } else {
    rawValue = field.defaultValue;
  }
  
  // Convert Firestore Timestamp objects to formatted date strings
  if (field.type === 'date' && rawValue) {
    // Check if it's a Firestore Timestamp object
    if (rawValue && typeof rawValue === 'object' && 'toDate' in rawValue) {
      const date = rawValue.toDate();
      // Use the field's format if specified, otherwise use default
      const formatStr = field.format || 'MM/DD/YYYY';
      
      // Simple date formatting based on format string
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      
      if (formatStr === 'YYYY') {
        return String(year);
      } else if (formatStr === 'MM/DD/YYYY') {
        return `${month}/${day}/${year}`;
      } else if (formatStr === 'DD/MM/YYYY') {
        return `${day}/${month}/${year}`;
      } else {
        // Default format
        return `${month}/${day}/${year}`;
      }
    }
    // If it's already a string (like from sample data), return as-is
    if (typeof rawValue === 'string') {
      return rawValue;
    }
    // If it's a Date object
    if (rawValue instanceof Date) {
      const month = String(rawValue.getMonth() + 1).padStart(2, '0');
      const day = String(rawValue.getDate()).padStart(2, '0');
      const year = rawValue.getFullYear();
      const formatStr = field.format || 'MM/DD/YYYY';
      
      if (formatStr === 'YYYY') {
        return String(year);
      } else if (formatStr === 'MM/DD/YYYY') {
        return `${month}/${day}/${year}`;
      } else if (formatStr === 'DD/MM/YYYY') {
        return `${day}/${month}/${year}`;
      } else {
        return `${month}/${day}/${year}`;
      }
    }
  }
  
  // Return the raw value for non-date fields (or undefined if no value)
  return rawValue;
}
